import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  getSpeedValueForPoint,
  formatSpeedDisplay,
  generateTooltipText,
  getLegendItemColor,
  prepareLegendItemsData
} from 'utils/overlayLegendUtils';
import { SpectrumLegendContainer, SpectrumLegend } from './OverlayTraceLegend.styled';
import LegendLoadingState from './LegendLoadingState';
import LegendItemRow from './LegendItemRow';

const OverlayTraceLegend = ({
  chartData,
  chartColors,
  timezone,
  isTraceOverlay,
  showOverlay,
  selectedPoints,
  legendPoints,
  traceDataKey,
  type,
  yUnit,
  updateSelectedPoints,
  retryOverlayTrace,
  multipleSpeedMarkers,
  assetType,
  config,
  shaftSpeedUnit,
  validPointsForLegend,
  hoveredLegendItem,
  activeLegendItem,
  onHoveredLegendItemChange,
  onActiveLegendItemChange,
  loadingPlaceholder,
  chartHeight,
  optionsVisible
}) => {
  const { t } = useTranslation();
  const [editingDescriptionIndex, setEditingDescriptionIndex] = useState(null);
  const [tempDescription, setTempDescription] = useState('');

  const getLegendPointByIndex = index => legendPoints[index] || null;

  const setHoveredLegend = (index) => {
    if (onHoveredLegendItemChange) {
      onHoveredLegendItemChange(index);
    }
  };

  const setActiveLegend = (index) => {
    if (onActiveLegendItemChange) {
      onActiveLegendItemChange(index);
    }
  };

  const handleLegendItemHover = (index) => {
    if (activeLegendItem !== index) {
      setHoveredLegend(index);
    }
  };

  const handleLegendItemLeave = () => {
    setHoveredLegend(null);
  };

  const removeLegendPointAtIndex = (index) => {
    if (!showOverlay || !selectedPoints || !updateSelectedPoints) return;

    const pointToRemove = getLegendPointByIndex(index);
    if (!pointToRemove) return;

    const newSelectedPoints = selectedPoints.filter(point => point !== pointToRemove);
    let nextHoveredLegendItem = hoveredLegendItem;
    if (hoveredLegendItem === index) {
      nextHoveredLegendItem = null;
    } else if (hoveredLegendItem !== null && hoveredLegendItem > index) {
      nextHoveredLegendItem = hoveredLegendItem - 1;
    }
    setHoveredLegend(nextHoveredLegendItem);
    let nextActiveLegendItem = activeLegendItem;
    if (activeLegendItem === index) {
      nextActiveLegendItem = null;
    } else if (activeLegendItem > index) {
      nextActiveLegendItem = activeLegendItem - 1;
    }
    setActiveLegend(nextActiveLegendItem);
    updateSelectedPoints(newSelectedPoints);
  };

  const handleLegendItemClick = (index, e) => {
    if ((e && (e.ctrlKey || e.metaKey || e.target.closest('.close-icon'))) ||
      e.target.closest('[data-action="remove"]')) {
      if (isTraceOverlay) {
        removeLegendPointAtIndex(index);
      }
      return;
    }

    setHoveredLegend(null);
    setActiveLegend(activeLegendItem === index ? null : index);
  };

  const handleRemoveLegendItem = (index, e) => {
    e.stopPropagation();
    if (isTraceOverlay) {
      removeLegendPointAtIndex(index);
    }
  };

  const handleEditDescription = (index) => {
    if (!isTraceOverlay) return;
    const point = getLegendPointByIndex(index);
    if (!point) return;
    setEditingDescriptionIndex(index);
    setTempDescription(point.description || '');
  };

  const handleSaveDescription = (index) => {
    if (!isTraceOverlay || !updateSelectedPoints) {
      setEditingDescriptionIndex(null);
      setTempDescription('');
      return;
    }

    const pointToUpdate = getLegendPointByIndex(index);
    if (pointToUpdate) {
      const pointIndex = selectedPoints.findIndex(p => p === pointToUpdate);
      if (pointIndex !== -1) {
        const newSelectedPoints = [...selectedPoints];
        newSelectedPoints[pointIndex] = {
          ...newSelectedPoints[pointIndex],
          description: tempDescription
        };
        updateSelectedPoints(newSelectedPoints);
      }
    }

    setEditingDescriptionIndex(null);
    setTempDescription('');
  };

  const handleDescriptionChange = (e) => {
    setTempDescription(e.target.value);
  };

  const handleDescriptionKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      handleSaveDescription(index);
    } else if (e.key === 'Escape') {
      setEditingDescriptionIndex(null);
      setTempDescription('');
    }
  };

  const getTraceStatusText = (point) => {
    if (!point || !point.traceStatus) return null;
    const traceType = traceDataKey === 'waveform_data' ? 'waveform' : 'spectrum';
    const status = point.traceStatus[traceType];
    if (status === 'loading') {
      return t('charts.overlay.measurementLoading');
    }
    if (status === 'error') {
      return t('charts.overlay.measurementLoadFailed');
    }
    return null;
  };

  const handleRetryOverlayTrace = (point, e) => {
    if (e) e.stopPropagation();
    if (retryOverlayTrace && point) {
      retryOverlayTrace(point);
    }
  };

  const speedContext = {
    validPointsForLegend,
    assetType,
    showOverlay,
    multipleSpeedMarkers,
    config
  };

  const formatSpeed = speedValue => formatSpeedDisplay({
    speedValue,
    shaftSpeedUnit,
    config
  });

  const getSpeedValue = (point, index) => getSpeedValueForPoint({
    point,
    index,
    ...speedContext
  });

  const getColor = (point, index) => getLegendItemColor({
    point,
    index,
    showOverlay,
    chartColors
  });

  const getTooltip = (point, index, speedValue) => generateTooltipText({
    point,
    index,
    speedValue,
    timezone,
    t,
    showOverlay,
    selectedPoints,
    chartData,
    type,
    yUnit,
    formatSpeed
  });

  const legendItems = prepareLegendItemsData({
    isTraceOverlay,
    legendPoints,
    chartData,
    selectedPoints,
    getSpeedValue,
    getColor,
    getTooltip,
    getTraceStatusText
  });

  const legendContent = (
    <SpectrumLegendContainer>
      <SpectrumLegend>
        {legendItems.map(itemData => (
          <LegendItemRow
            key={itemData.pointToShow && itemData.pointToShow.measurement_id != null
              ? `m-${itemData.pointToShow.measurement_id}`
              : `i-${itemData.index}`}
            itemData={itemData}
            isTraceOverlay={isTraceOverlay}
            showOverlay={showOverlay}
            selectedPoints={selectedPoints}
            timezone={timezone}
            type={type}
            yUnit={yUnit}
            chartData={chartData}
            traceDataKey={traceDataKey}
            shaftSpeedUnit={shaftSpeedUnit}
            config={config}
            hoveredLegendItem={hoveredLegendItem}
            activeLegendItem={activeLegendItem}
            editingDescriptionIndex={editingDescriptionIndex}
            tempDescription={tempDescription}
            retryOverlayTrace={retryOverlayTrace}
            onLegendItemHover={handleLegendItemHover}
            onLegendItemLeave={handleLegendItemLeave}
            onLegendItemClick={handleLegendItemClick}
            onEditDescription={handleEditDescription}
            onRemoveLegendItem={handleRemoveLegendItem}
            onDescriptionChange={handleDescriptionChange}
            onSaveDescription={handleSaveDescription}
            onDescriptionKeyDown={handleDescriptionKeyDown}
            onRetryOverlayTrace={handleRetryOverlayTrace}
          />
        ))}
      </SpectrumLegend>
    </SpectrumLegendContainer>
  );

  if (loadingPlaceholder) {
    return (
      <LegendLoadingState
        optionsVisible={optionsVisible}
        chartHeight={chartHeight}
        legendContent={legendContent}
      />
    );
  }

  return legendContent;
};

OverlayTraceLegend.propTypes = {
  chartData: PropTypes.array,
  chartColors: PropTypes.array,
  timezone: PropTypes.string.isRequired,
  isTraceOverlay: PropTypes.bool,
  showOverlay: PropTypes.bool,
  selectedPoints: PropTypes.array,
  legendPoints: PropTypes.array,
  traceDataKey: PropTypes.string,
  type: PropTypes.string,
  yUnit: PropTypes.string,
  updateSelectedPoints: PropTypes.func,
  retryOverlayTrace: PropTypes.func,
  multipleSpeedMarkers: PropTypes.array,
  assetType: PropTypes.string,
  config: PropTypes.object,
  shaftSpeedUnit: PropTypes.string,
  validPointsForLegend: PropTypes.array,
  hoveredLegendItem: PropTypes.number,
  activeLegendItem: PropTypes.number,
  onHoveredLegendItemChange: PropTypes.func,
  onActiveLegendItemChange: PropTypes.func,
  loadingPlaceholder: PropTypes.bool,
  chartHeight: PropTypes.string,
  optionsVisible: PropTypes.bool
};

OverlayTraceLegend.defaultProps = {
  chartData: [],
  chartColors: [],
  isTraceOverlay: false,
  showOverlay: false,
  selectedPoints: [],
  legendPoints: [],
  traceDataKey: null,
  type: null,
  yUnit: null,
  updateSelectedPoints: null,
  retryOverlayTrace: null,
  multipleSpeedMarkers: [],
  assetType: null,
  config: {},
  shaftSpeedUnit: null,
  validPointsForLegend: [],
  hoveredLegendItem: null,
  activeLegendItem: null,
  onHoveredLegendItemChange: null,
  onActiveLegendItemChange: null,
  loadingPlaceholder: false,
  chartHeight: null,
  optionsVisible: false
};

export default OverlayTraceLegend;
